import {
  Badge,
  Button,
  Group,
  Input,
  Paper,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
  Pagination,
  ActionIcon,
  Box,
  Skeleton,
  Alert,
  LoadingOverlay,
} from "@mantine/core";
import {
  IconSearch,
  IconFilter,
  IconX,
  IconUsers,
  IconActivity,
  IconHeartbeat,
  IconVaccine,
  IconPhoto,
  IconChevronDown,
  IconAlertTriangle,
  IconRefresh,
} from "@tabler/icons-react";
import { useState, useMemo } from "react";
import { usePatients, usePatientSearch } from "../../hooks/usePatients";
import { usePatientStore } from "../../stores/patientStore";
import { PatientListSkeleton } from "../skeletons/PatientListSkeleton";
import type { PatientsListItem } from "../../services/patients";

interface EnhancedPatientSelectorProps {
  selectedPatientId?: string;
  onPatientSelect: (patientId: string) => void;
  showDataIndicators?: boolean;
  showSearch?: boolean;
  showFilters?: boolean;
  showPagination?: boolean;
  maxHeight?: number;
  placeholder?: string;
}

export function EnhancedPatientSelector({
  selectedPatientId,
  onPatientSelect,
  showDataIndicators = true,
  showSearch = true,
  showFilters = true,
  showPagination = true,
  maxHeight = 400,
  placeholder: _placeholder = "Select patient",
}: EnhancedPatientSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Store state for filters
  const searchFilters = usePatientStore((state) => state.searchFilters);
  const setSearchFilters = usePatientStore((state) => state.setSearchFilters);
  const clearSearchFilters = usePatientStore(
    (state) => state.clearSearchFilters,
  );

  // Build search request
  const searchRequest = useMemo(
    () => ({
      query: searchQuery || undefined,
      cohort: searchFilters.cohort || "all",
      gender: searchFilters.gender,
      minAge: searchFilters.minAge,
      maxAge: searchFilters.maxAge,
      hasGlucoseData: searchFilters.hasGlucoseData,
      hasEcgData: searchFilters.hasEcgData,
      hasInsulinData: searchFilters.hasInsulinData,
      hasMealImages: searchFilters.hasMealImages,
      minDataCompleteness: searchFilters.minDataCompleteness,
      page: currentPage,
      limit: pageSize,
    }),
    [searchQuery, searchFilters, currentPage],
  );

  // Use search if there are filters or query, otherwise use basic patients
  const hasActiveFilters =
    searchQuery ||
    Object.keys(searchFilters).some(
      (key) => searchFilters[key as keyof typeof searchFilters] !== undefined,
    );

  const {
    data: searchResults,
    isLoading: searchLoading,
    error: searchError,
  } = usePatientSearch(hasActiveFilters ? searchRequest : ({} as any));

  const {
    data: patientsData,
    isLoading: patientsLoading,
    error: patientsError,
  } = usePatients(
    hasActiveFilters ? {} : { page: currentPage, limit: pageSize },
  );

  const isLoading = hasActiveFilters ? searchLoading : patientsLoading;
  const error = hasActiveFilters ? searchError : patientsError;

  // Enhanced error handling
  const getErrorMessage = (error: any) => {
    if (!error) return null;

    if (error instanceof Error) {
      if (error.message.includes("Authentication")) {
        return "Authentication failed. Please check your credentials.";
      }
      if (error.message.includes("Access denied")) {
        return "You do not have permission to view patient data.";
      }
      if (error.message.includes("Network")) {
        return "Network error. Please check your connection and try again.";
      }
      if (error.message.includes("Server error")) {
        return "Server is currently unavailable. Please try again later.";
      }
      return error.message;
    }
    return "An unexpected error occurred while loading patients.";
  };

  const errorMessage = getErrorMessage(error);
  const canRetry =
    error &&
    !error?.message?.includes("Authentication") &&
    !error?.message?.includes("Access denied");

  const patients = useMemo(() => {
    if (hasActiveFilters && searchResults?.searchResults) {
      return searchResults.searchResults.patients;
    }
    return patientsData?.patients || [];
  }, [hasActiveFilters, searchResults, patientsData]);

  const paginationData = useMemo(() => {
    if (hasActiveFilters && searchResults?.searchResults) {
      return searchResults.searchResults.pagination;
    }
    return {
      page: currentPage,
      limit: pageSize,
      total: patientsData?.patients?.length || 0,
      totalPages: Math.ceil((patientsData?.patients?.length || 0) / pageSize),
      hasNext: false,
      hasPrev: currentPage > 1,
    };
  }, [hasActiveFilters, searchResults, patientsData, currentPage, pageSize]);

  const handleFilterChange = (key: string, value: any) => {
    setSearchFilters({ [key]: value });
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    clearSearchFilters();
    setSearchQuery("");
    setCurrentPage(1);
  };

  const getDataCompletenessColor = (score: number) => {
    if (score >= 75) return "green";
    if (score >= 50) return "yellow";
    if (score >= 25) return "orange";
    return "red";
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!patients.length) return null;

    const totalPatients = patients.length;
    const withGlucose = patients.filter(
      (p) => (p.glucoseReadings || 0) > 0,
    ).length;
    const withECG = patients.filter((p) => (p.ecgReadings || 0) > 0).length;
    const withInsulin = patients.filter(
      (p) => (p.insulinMeasurements || 0) > 0,
    ).length;
    const withMeals = patients.filter((p) => (p.mealImages || 0) > 0).length;
    const diabetesCohort = patients.filter(
      (p) => p.cohort === "diabetes",
    ).length;
    const healthyCohort = patients.filter((p) => p.cohort === "healthy").length;
    const avgCompleteness =
      patients.reduce((sum, p) => sum + (p.dataCompletenessScore || 0), 0) /
      totalPatients;

    return {
      totalPatients,
      withGlucose,
      withECG,
      withInsulin,
      withMeals,
      diabetesCohort,
      healthyCohort,
      avgCompleteness: Math.round(avgCompleteness),
    };
  }, [patients]);

  const PatientItem = ({ patient }: { patient: PatientsListItem }) => {
    const totalDataPoints =
      (patient.glucoseReadings || 0) +
      (patient.ecgReadings || 0) +
      (patient.insulinMeasurements || 0) +
      (patient.mealImages || 0);

    const hasHighDataVolume = totalDataPoints > 1000;
    const hasSomeData = totalDataPoints > 0;

    return (
      <Paper
        p="sm"
        mb="xs"
        withBorder
        style={{
          cursor: "pointer",
          backgroundColor:
            selectedPatientId === patient.patientId
              ? "var(--mantine-color-blue-light)"
              : undefined,
          border: hasHighDataVolume
            ? "2px solid var(--mantine-color-yellow-6)"
            : undefined,
        }}
        onClick={() => onPatientSelect(patient.patientId)}
      >
        <Group justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Group gap="xs" mb="xs">
              <Text fw={600}>{patient.name}</Text>
              <Text size="sm" c="dimmed">
                ({patient.patientId})
              </Text>
              <Badge
                size="xs"
                color={
                  patient.ecgReadings && patient.ecgReadings > 0
                    ? "green"
                    : "gray"
                }
                leftSection={<IconHeartbeat size={10} />}
              >
                {patient.ecgReadings || 0} ECG
              </Badge>
              {hasHighDataVolume && (
                <Tooltip label="High data volume patient">
                  <Badge color="yellow" variant="dot" size="xs">
                    Rich Data
                  </Badge>
                </Tooltip>
              )}
            </Group>

            <Group gap="md" mb="xs">
              <Text size="sm" c="dimmed">
                {patient.age}y • {patient.gender}
              </Text>
              <Text size="sm" c="dimmed">
                {patient.condition}
              </Text>
              {patient.earliestReading && patient.latestReading && (
                <Tooltip
                  label={`Data range: ${patient.earliestReading} to ${patient.latestReading}`}
                >
                  <Text size="xs" c="blue">
                    📅{" "}
                    {Math.ceil(
                      (new Date(patient.latestReading).getTime() -
                        new Date(patient.earliestReading).getTime()) /
                        (1000 * 60 * 60 * 24),
                    )}{" "}
                    days
                  </Text>
                </Tooltip>
              )}
            </Group>

            {showDataIndicators && (
              <Group gap="xs" mt="xs" wrap="nowrap">
                <Tooltip
                  label={`Glucose Readings: ${patient.glucoseReadings || 0} records`}
                >
                  <Badge
                    color={patient.glucoseReadings > 0 ? "blue" : "gray"}
                    variant={patient.glucoseReadings > 500 ? "filled" : "light"}
                    size="xs"
                    leftSection={<IconActivity size={10} />}
                  >
                    {patient.glucoseReadings || 0}
                  </Badge>
                </Tooltip>

                <Tooltip
                  label={`ECG Readings: ${patient.ecgReadings || 0} records`}
                >
                  <Badge
                    color={(patient.ecgReadings || 0) > 0 ? "red" : "gray"}
                    variant={
                      (patient.ecgReadings || 0) > 500 ? "filled" : "light"
                    }
                    size="xs"
                    leftSection={<IconHeartbeat size={10} />}
                  >
                    {patient.ecgReadings || 0}
                  </Badge>
                </Tooltip>

                <Tooltip
                  label={`Insulin Measurements: ${patient.insulinMeasurements || 0} records`}
                >
                  <Badge
                    color={
                      (patient.insulinMeasurements || 0) > 0 ? "green" : "gray"
                    }
                    variant={
                      (patient.insulinMeasurements || 0) > 100
                        ? "filled"
                        : "light"
                    }
                    size="xs"
                    leftSection={<IconVaccine size={10} />}
                  >
                    {patient.insulinMeasurements || 0}
                  </Badge>
                </Tooltip>

                <Tooltip
                  label={`Meal Images: ${patient.mealImages || 0} records`}
                >
                  <Badge
                    color={(patient.mealImages || 0) > 0 ? "orange" : "gray"}
                    variant={
                      (patient.mealImages || 0) > 50 ? "filled" : "light"
                    }
                    size="xs"
                    leftSection={<IconPhoto size={10} />}
                  >
                    {patient.mealImages || 0}
                  </Badge>
                </Tooltip>

                <Tooltip
                  label={`Data Completeness: ${patient.dataCompletenessScore || 0}%`}
                >
                  <Badge
                    color={getDataCompletenessColor(
                      patient.dataCompletenessScore || 0,
                    )}
                    variant="dot"
                    size="xs"
                  >
                    {patient.dataCompletenessScore || 0}%
                  </Badge>
                </Tooltip>

                {totalDataPoints > 0 && (
                  <Tooltip
                    label={`Total data points: ${totalDataPoints.toLocaleString()}`}
                  >
                    <Badge color="grape" variant="light" size="xs">
                      Σ{" "}
                      {totalDataPoints > 999
                        ? `${(totalDataPoints / 1000).toFixed(1)}k`
                        : totalDataPoints}
                    </Badge>
                  </Tooltip>
                )}
              </Group>
            )}
          </div>

          <Group gap="xs">
            {selectedPatientId === patient.patientId && (
              <Badge color="blue" variant="filled">
                Selected
              </Badge>
            )}
            {!hasSomeData && (
              <Tooltip label="No data available">
                <Badge color="gray" variant="outline" size="xs">
                  No Data
                </Badge>
              </Tooltip>
            )}
          </Group>
        </Group>
      </Paper>
    );
  };

  return (
    <Stack gap="md">
      {/* Summary Stats */}
      {summaryStats && !hasActiveFilters && (
        <Paper p="md" withBorder bg="var(--mantine-color-gray-light)">
          <Group justify="space-between" align="center">
            <Group gap="lg">
              <Group gap="xs">
                <IconUsers size={16} />
                <Text size="sm" fw={600}>
                  {summaryStats.totalPatients} Patients
                </Text>
              </Group>

              <Group gap="xs">
                <Badge color="red" size="xs" variant="light">
                  {summaryStats.diabetesCohort} Diabetes
                </Badge>
                <Badge color="green" size="xs" variant="light">
                  {summaryStats.healthyCohort} Healthy
                </Badge>
              </Group>

              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  Data:
                </Text>
                <Badge color="blue" size="xs" variant="light">
                  {summaryStats.withGlucose} Glucose
                </Badge>
                <Badge color="red" size="xs" variant="light">
                  {summaryStats.withECG} ECG
                </Badge>
                <Badge color="green" size="xs" variant="light">
                  {summaryStats.withInsulin} Insulin
                </Badge>
                <Badge color="orange" size="xs" variant="light">
                  {summaryStats.withMeals} Meals
                </Badge>
              </Group>
            </Group>

            <Badge
              color={getDataCompletenessColor(summaryStats.avgCompleteness)}
              variant="dot"
              size="sm"
            >
              {summaryStats.avgCompleteness}% Avg Complete
            </Badge>
          </Group>
        </Paper>
      )}

      {/* Search Bar */}
      {showSearch && (
        <Group>
          <Input
            placeholder="Search patients by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftSection={
              isLoading ? (
                <Skeleton width={16} height={16} />
              ) : (
                <IconSearch size={16} />
              )
            }
            rightSection={
              <Group gap="xs">
                {isLoading && <Skeleton width={40} height={16} />}
                {searchQuery && !isLoading && (
                  <ActionIcon size="sm" onClick={() => setSearchQuery("")}>
                    <IconX size={12} />
                  </ActionIcon>
                )}
              </Group>
            }
            style={{ flex: 1 }}
            disabled={isLoading}
          />

          {showFilters && (
            <Button
              variant="light"
              leftSection={<IconFilter size={16} />}
              rightSection={<IconChevronDown size={12} />}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              disabled={isLoading}
              loading={isLoading}
            >
              Filters
            </Button>
          )}
        </Group>
      )}

      {/* Advanced Filters */}
      {showFilters && showAdvancedFilters && (
        <Paper p="md" withBorder>
          <Group justify="space-between" align="center" mb="md">
            <Title order={6}>Advanced Filters</Title>
            <Button
              variant="outline"
              size="xs"
              onClick={clearFilters}
              leftSection={<IconX size={12} />}
            >
              Clear All
            </Button>
          </Group>

          <Group grow>
            <Select
              label="Cohort"
              placeholder="All cohorts"
              data={[
                { value: "all", label: "All Cohorts" },
                { value: "diabetes", label: "Diabetes" },
                { value: "healthy", label: "Healthy" },
              ]}
              value={searchFilters.cohort || "all"}
              onChange={(value) => handleFilterChange("cohort", value)}
            />

            <Select
              label="Gender"
              placeholder="All genders"
              data={[
                { value: "", label: "All Genders" },
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
                { value: "Other", label: "Other" },
              ]}
              value={searchFilters.gender || ""}
              onChange={(value) =>
                handleFilterChange("gender", value || undefined)
              }
            />
          </Group>

          <Group grow mt="md">
            <Input.Wrapper label="Min Age">
              <Input
                type="number"
                placeholder="Min age"
                value={searchFilters.minAge || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "minAge",
                    e.target.value ? parseInt(e.target.value) : undefined,
                  )
                }
              />
            </Input.Wrapper>

            <Input.Wrapper label="Max Age">
              <Input
                type="number"
                placeholder="Max age"
                value={searchFilters.maxAge || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "maxAge",
                    e.target.value ? parseInt(e.target.value) : undefined,
                  )
                }
              />
            </Input.Wrapper>
          </Group>

          <Group grow mt="md">
            <Select
              label="Min Data Completeness"
              placeholder="Any completeness"
              data={[
                { value: "", label: "Any" },
                { value: "25", label: "25% or more" },
                { value: "50", label: "50% or more" },
                { value: "75", label: "75% or more" },
                { value: "90", label: "90% or more" },
              ]}
              value={searchFilters.minDataCompleteness?.toString() || ""}
              onChange={(value) =>
                handleFilterChange(
                  "minDataCompleteness",
                  value ? parseInt(value) : undefined,
                )
              }
            />
          </Group>

          <Title order={6} mt="lg" mb="sm">
            Data Availability
          </Title>
          <Group>
            <Button
              variant={searchFilters.hasGlucoseData ? "filled" : "outline"}
              size="xs"
              color="blue"
              onClick={() =>
                handleFilterChange(
                  "hasGlucoseData",
                  !searchFilters.hasGlucoseData,
                )
              }
            >
              Has Glucose
            </Button>
            <Button
              variant={searchFilters.hasEcgData ? "filled" : "outline"}
              size="xs"
              color="red"
              onClick={() =>
                handleFilterChange("hasEcgData", !searchFilters.hasEcgData)
              }
            >
              Has ECG
            </Button>
            <Button
              variant={searchFilters.hasInsulinData ? "filled" : "outline"}
              size="xs"
              color="green"
              onClick={() =>
                handleFilterChange(
                  "hasInsulinData",
                  !searchFilters.hasInsulinData,
                )
              }
            >
              Has Insulin
            </Button>
            <Button
              variant={searchFilters.hasMealImages ? "filled" : "outline"}
              size="xs"
              color="orange"
              onClick={() =>
                handleFilterChange(
                  "hasMealImages",
                  !searchFilters.hasMealImages,
                )
              }
            >
              Has Meals
            </Button>
          </Group>
        </Paper>
      )}

      {/* Patient List */}
      <Box pos="relative" style={{ maxHeight, overflowY: "auto" }}>
        <LoadingOverlay visible={isLoading} overlayProps={{ blur: 2 }} />

        {!isLoading && error ? (
          <Alert
            color="red"
            title="Error Loading Patients"
            icon={<IconAlertTriangle size={16} />}
            mb="md"
          >
            <Text size="sm" mb="sm">
              {errorMessage}
            </Text>
            {canRetry && (
              <Button
                size="xs"
                variant="outline"
                leftSection={<IconRefresh size={12} />}
                onClick={() => {
                  // Refetch based on current state
                  if (hasActiveFilters && searchResults) {
                    // This would be handled by the query client
                  } else {
                    // Refetch patients
                  }
                }}
              >
                Retry
              </Button>
            )}
          </Alert>
        ) : !isLoading && patients.length === 0 ? (
          <Paper p="xl" withBorder style={{ textAlign: "center" }}>
            <IconUsers
              size={48}
              color="var(--mantine-color-gray-4)"
              style={{ margin: "0 auto 1rem" }}
            />
            <Text size="lg" fw={600} c="dimmed" mb="xs">
              {hasActiveFilters
                ? "No patients match the current filters"
                : "No patients available"}
            </Text>
            {hasActiveFilters && (
              <Button
                size="sm"
                variant="outline"
                onClick={clearFilters}
                leftSection={<IconX size={14} />}
              >
                Clear Filters
              </Button>
            )}
          </Paper>
        ) : !isLoading ? (
          <Stack gap="xs">
            {patients.map((patient) => (
              <PatientItem key={patient.patientId} patient={patient} />
            ))}
          </Stack>
        ) : (
          <PatientListSkeleton
            count={5}
            showDataIndicators={showDataIndicators}
          />
        )}
      </Box>

      {/* Pagination */}
      {showPagination && paginationData.totalPages > 1 && (
        <Group justify="center" align="center">
          <Pagination
            total={paginationData.totalPages}
            value={paginationData.page}
            onChange={setCurrentPage}
            size="sm"
            siblings={1}
          />
          <Text size="xs" c="dimmed">
            Page {paginationData.page} of {paginationData.totalPages}
          </Text>
        </Group>
      )}

      {/* Results Summary */}
      {hasActiveFilters && (
        <Paper p="sm" withBorder bg="var(--mantine-color-blue-light)">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={600}>
              Search Results
            </Text>
            <Text size="xs" c="dimmed">
              Showing {patients.length} of {paginationData.total} patients
              {paginationData.totalPages > 1 &&
                ` (Page ${paginationData.page} of ${paginationData.totalPages})`}
            </Text>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
